import merge from 'lodash-es/merge';
import uniqueId from 'lodash-es/uniqueId';
import { reactive, readonly, ref, RenderFunction } from 'vue';

import { StatusSeverity } from '../../../types/statusLevels';

export const DEFAULT_TIMEOUT = 5000;

export type Toast = {
  id: string;
  text: string | RenderFunction;
  status: StatusSeverity;
  offsetTop?: string;
};

export interface ToastOptions {
  /**
   * The timeout in milliseconds before the toast is automatically dismissed. If set to `false`, the toast will persist until the user clicks on it.
   * @default DEFAULT_TIMEOUT
   */
  timeout?: number | boolean;
  /**
   * A group name ensures only one toast with the same group and status is visible at any given time.
   * Toasts with the same group but different statuses will be treated as separate and will display concurrently.
   */
  group?: string;
  /**
   * The container distance from the top of the viewport.
   */
  offsetTop?: string;
}

type ToastIdOrUndefined<O extends ToastOptions | undefined> = O extends { id: string }
  ? Toast['id'] | undefined
  : Toast['id'];

const active = ref<Toast[]>([]);

export default function useToasts(globalOptions: Omit<ToastOptions, 'group'> = { timeout: DEFAULT_TIMEOUT }) {
  // method overloads
  function remove(toastId: Toast['id']): void;
  function remove(options: { group: string; status: StatusSeverity }): void;
  function remove(toastIdOrOptions: Toast['id'] | { group: string; status: StatusSeverity }) {
    const toastId = typeof toastIdOrOptions === 'string' ? toastIdOrOptions : undefined;
    const { group = undefined, status = undefined } = typeof toastIdOrOptions === 'object' ? toastIdOrOptions : {};

    if (!toastId && (!group || !status)) {
      throw new Error('Must provide either a toast ID or both group and type');
    }

    const idToRemove = group && status ? `toast-${status}-${group}` : String(toastId);
    const index = active.value.findIndex(({ id }) => id === idToRemove);
    if (index === -1) {
      return;
    }

    active.value.splice(index, 1);
  }

  // method overloads
  function create(text: Toast['text'], status: StatusSeverity, options?: Omit<ToastOptions, 'group'>): Toast['id'];
  function create(
    text: Toast['text'],
    status: StatusSeverity,
    options: ToastOptions & { id: string },
  ): Toast['id'] | undefined;
  function create(text: Toast['text'], status: StatusSeverity, localOptions?: ToastOptions): Toast['id'] | undefined {
    const options = merge({}, globalOptions, localOptions);

    const id = options?.group ? `toast-${status}-${options.group}` : uniqueId('toast-');
    if (options?.group && active.value.some(({ id: existingId }) => existingId === id)) {
      return;
    }

    active.value.push({
      text,
      id,
      status,
      offsetTop: options.offsetTop,
    });

    if (typeof options.timeout === 'number') {
      setTimeout(() => remove(id), Math.abs(options.timeout));
    }

    return id;
  }

  function removeAll() {
    active.value = [];
  }

  function error<O extends ToastOptions>(text: Toast['text'], options?: O): ToastIdOrUndefined<O> {
    return create(text, 'error', options);
  }

  function info<O extends ToastOptions>(text: Toast['text'], options?: O): ToastIdOrUndefined<O> {
    return create(text, 'info', options);
  }

  function success<O extends ToastOptions>(text: Toast['text'], options?: O): ToastIdOrUndefined<O> {
    return create(text, 'success', options);
  }

  function warning<O extends ToastOptions>(text: Toast['text'], options?: O): ToastIdOrUndefined<O> {
    return create(text, 'warning', options);
  }

  return reactive({
    /**
     * The list of toasts that are currently visible.
     */
    active: readonly(active),

    /**
     * Removes an active/visible toast by id or by group and type.
     */
    remove,

    /**
     * Creates a new toast and makes it active/visible.
     */
    create,

    /**
     * Removes all active/visible toasts.
     */
    removeAll,

    /**
     * Creates a new "error" toast.
     */
    error,

    /**
     * Creates a new "info" toast.
     */
    info,

    /**
     * Creates a new "success" toast.
     */
    success,

    /**
     * Creates a new "warning" toast.
     */
    warning,
  });
}
